import axios from "axios";

const TOKEN_KEY = "token";

// ─── Public POST — no auth (adminSignIn) ─────────────────────────────────────
export async function gqlPublic(query, variables = {}) {
  const res = await axios.post(process.env.REACT_APP_BASEURL, { query, variables }, {
    headers: {
      "Content-Type": "application/json",
      "Apollo-Require-Preflight": "true",
    },
  });
  if (res.data?.errors?.length) throw new Error(res.data.errors[0].message);
  return res.data.data;
}

// ─── Authenticated POST — reads token from localStorage ───────────────────────
export async function gqlRequest(query, variables = {}, overrideToken = null) {
  const token = overrideToken || localStorage.getItem(TOKEN_KEY);
  try {
    const res = await axios.post(process.env.REACT_APP_BASEURL, { query, variables }, {
      headers: {
        "Content-Type": "application/json",
        "Apollo-Require-Preflight": "true",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (res.data?.errors?.length) throw new Error(res.data.errors[0].message);
    return res.data.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.clear();
      axios.defaults.headers.common.Authorization = "";
      window.location.reload();
      return;
    }
    throw error;
  }
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const ADMIN_SIGN_IN = `
  mutation AdminSignIn($login: String!, $password: String!) {
    adminSignIn(login: $login, password: $password) {
      token
      lastlogin
      isenabletwofactorauth
    }
  }
`;

export const VERIFY_AUTH_TOKEN = `
  mutation verifytwofactorauth($code: String) {
    verifytwofactorauth(code: $code) {
      token
      lastlogin
      isenabletwofactorauth
    }
  }
`;

// ── Entities ──────────────────────────────────────────────────────────────────
export const GET_BUSINESSES_WITH_PAGING = `
  query BusinessesWithPaging($pageNumber: Int!, $pageSize: Int!, $searchText: String) {
    businessesWithPaging(pageNumber: $pageNumber, pageSize: $pageSize, searchText: $searchText) {
      count
      rows {
        entityid
        businessname
        brandname
        firstname
        lastname
        email
        phonenumber
        entitytypeid
        status
        isbusinessverified
        logourl
        subdomain
        createdAt
      }
    }
  }
`;

// ── Business Usage ────────────────────────────────────────────────────────────
export const GET_BUSINESS_USAGES = `
  query Businessusagesbybusiness($businessid: ID!) {
    businessusagesbybusiness(businessid: $businessid) {
      businessusageid
      businessid
      locationid
      trialplanid
      curruntplanid
      subscriptionid
      isfreeplan
      plantype
      paymentid
      userslimit
      textsmslimit
      emaillimit
      storagelimit
      calltimelimit
      textsms
      users
      email
      storage
      calltime
      leadgatheringlimit
      leadgathering
      trialplanenddate
      startdate
      enddate
      isfullaccess
      isactive
      deleted
      isunlimitedusers
      isunlimitedtextsms
      isunlimitedemail
      isunlimitedstorage
      isunlimitedcalltime
      isunlimitedleadgathering
      isbusinessverified
      socialpostlimit
      socialpost
      isunlimitedsocialpost
      emailfinderlimit
      emailfinder
      isunlimitedemailfinder
      leadfinderlimit
      leadfinder
      isunlimitedleadfinder
      aiquerylimit
      aiquery
      linkedinuserlimit
      linkedinuser
      whatsappuserlimit
      whatsappuser
      instagramuserlimit
      instagramuser
      isunlimitedaiquery
      enrichsearchlimit
      enrichsearch
      enrichreveallimit
      enrichreveal
    }
  }
`;

export const CREATE_BUSINESS_USAGE = `
  mutation Createbusinessusage(
    $businessid: ID!, $startdate: String!,
    $trialplanid: Int, $locationid: ID, $curruntplanid: Int,
    $subscriptionid: ID, $isfreeplan: Boolean, $plantype: Int,
    $isactive: Boolean, $paymentid: String, $deleted: Boolean,
    $textsmslimit: Int, $emaillimit: Int, $storagelimit: Int,
    $calltimelimit: Int, $textsms: Int, $email: Int, $storage: Float,
    $calltime: Int, $userslimit: Int, $users: Int,
    $leadgatheringlimit: Int, $leadgathering: Int,
    $isfullaccess: Boolean, $trialplanenddate: String, $enddate: String,
    $isunlimitedusers: Boolean, $isunlimitedtextsms: Boolean,
    $isunlimitedemail: Boolean, $isunlimitedstorage: Boolean,
    $isunlimitedcalltime: Boolean, $isunlimitedleadgathering: Boolean,
    $socialpostlimit: Int, $socialpost: Int, $isunlimitedsocialpost: Boolean,
    $emailfinderlimit: Int, $emailfinder: Int, $isunlimitedemailfinder: Boolean,
    $leadfinderlimit: Int, $leadfinder: Int, $isunlimitedleadfinder: Boolean,
    $aiquerylimit: Int, $aiquery: Int, $isunlimitedaiquery: Boolean,
    $linkedinuserlimit: Int, $linkedinuser: Int,
    $instagramuserlimit: Int, $instagramuser: Int,
    $whatsappuserlimit: Int, $whatsappuser: Int,
    $enrichsearchlimit: Int, $enrichsearch: Int,
    $enrichreveallimit: Int, $enrichreveal: Int
  ) {
    createbusinessusage(
      businessid: $businessid, startdate: $startdate,
      trialplanid: $trialplanid, locationid: $locationid,
      curruntplanid: $curruntplanid, subscriptionid: $subscriptionid,
      isfreeplan: $isfreeplan, plantype: $plantype,
      isactive: $isactive, paymentid: $paymentid, deleted: $deleted,
      textsmslimit: $textsmslimit, emaillimit: $emaillimit,
      storagelimit: $storagelimit, calltimelimit: $calltimelimit,
      textsms: $textsms, email: $email, storage: $storage,
      calltime: $calltime, userslimit: $userslimit, users: $users,
      leadgatheringlimit: $leadgatheringlimit, leadgathering: $leadgathering,
      isfullaccess: $isfullaccess, trialplanenddate: $trialplanenddate,
      enddate: $enddate, isunlimitedusers: $isunlimitedusers,
      isunlimitedtextsms: $isunlimitedtextsms, isunlimitedemail: $isunlimitedemail,
      isunlimitedstorage: $isunlimitedstorage, isunlimitedcalltime: $isunlimitedcalltime,
      isunlimitedleadgathering: $isunlimitedleadgathering,
      socialpostlimit: $socialpostlimit, socialpost: $socialpost,
      isunlimitedsocialpost: $isunlimitedsocialpost,
      emailfinderlimit: $emailfinderlimit, emailfinder: $emailfinder,
      isunlimitedemailfinder: $isunlimitedemailfinder,
      leadfinderlimit: $leadfinderlimit, leadfinder: $leadfinder,
      isunlimitedleadfinder: $isunlimitedleadfinder,
      aiquerylimit: $aiquerylimit, aiquery: $aiquery,
      isunlimitedaiquery: $isunlimitedaiquery,
      linkedinuserlimit: $linkedinuserlimit, linkedinuser: $linkedinuser,
      instagramuserlimit: $instagramuserlimit, instagramuser: $instagramuser
      whatsappuserlimit: $whatsappuserlimit, whatsappuser: $whatsappuser,
      enrichsearchlimit: $enrichsearchlimit, enrichsearch: $enrichsearch,
      enrichreveallimit: $enrichreveallimit, enrichreveal: $enrichreveal
    ) {
      businessusageid businessid startdate enddate isactive isfreeplan plantype
    }
  }
`;

export const CREATE_MULTIPLE_BUSINESS_USAGE = `
  mutation Createmultiplebusinessusage(
    $businessid: ID!, $planinterval: Int!, $startdate: String!,
    $locationid: ID, $curruntplanid: Int, $plantype: Int, $isactive: Boolean,
    $textsmslimit: Int, $emaillimit: Int, $storagelimit: Int, $calltimelimit: Int,
    $userslimit: Int, $leadgatheringlimit: Int, $isfullaccess: Boolean, $enddate: String,
    $isunlimitedusers: Boolean, $isunlimitedtextsms: Boolean,
    $isunlimitedemail: Boolean, $isunlimitedstorage: Boolean,
    $isunlimitedcalltime: Boolean, $isunlimitedleadgathering: Boolean,
    $socialpostlimit: Int, $isunlimitedsocialpost: Boolean,
    $emailfinderlimit: Int, $isunlimitedemailfinder: Boolean,
    $leadfinderlimit: Int, $isunlimitedleadfinder: Boolean,
    $aiquerylimit: Int, $isunlimitedaiquery: Boolean,
    $linkedinuserlimit: Int, $whatsappuserlimit: Int,
    $instagramuserlimit: Int,
    $enrichsearchlimit: Int, $enrichreveallimit: Int
  ) {
    createmultiplebusinessusage(
      businessid: $businessid, planinterval: $planinterval, startdate: $startdate,
      locationid: $locationid, curruntplanid: $curruntplanid, plantype: $plantype,
      isactive: $isactive, textsmslimit: $textsmslimit, emaillimit: $emaillimit,
      storagelimit: $storagelimit, calltimelimit: $calltimelimit,
      userslimit: $userslimit, leadgatheringlimit: $leadgatheringlimit,
      isfullaccess: $isfullaccess, enddate: $enddate,
      isunlimitedusers: $isunlimitedusers, isunlimitedtextsms: $isunlimitedtextsms,
      isunlimitedemail: $isunlimitedemail, isunlimitedstorage: $isunlimitedstorage,
      isunlimitedcalltime: $isunlimitedcalltime,
      isunlimitedleadgathering: $isunlimitedleadgathering,
      socialpostlimit: $socialpostlimit, isunlimitedsocialpost: $isunlimitedsocialpost,
      emailfinderlimit: $emailfinderlimit, isunlimitedemailfinder: $isunlimitedemailfinder,
      leadfinderlimit: $leadfinderlimit, isunlimitedleadfinder: $isunlimitedleadfinder,
      aiquerylimit: $aiquerylimit, isunlimitedaiquery: $isunlimitedaiquery,
      linkedinuserlimit: $linkedinuserlimit, whatsappuserlimit: $whatsappuserlimit,
      instagramuserlimit: $instagramuserlimit,
      enrichsearchlimit: $enrichsearchlimit, enrichreveallimit: $enrichreveallimit
    ) {
      businessusageid
      businessid
      startdate
      enddate
      isactive
      plantype
      curruntplanid
    }
  }
`;
export const UPDATE_BUSINESS_USAGE = `
  mutation Updatebusinessusagebyid(
    $businessusageid: ID!, $startdate: String,
    $trialplanid: Int, $curruntplanid: Int,
    $subscriptionid: ID, $isfreeplan: Boolean, $plantype: Int,
    $isactive: Boolean, $paymentid: String, $deleted: Boolean,
    $textsmslimit: Int, $emaillimit: Int, $storagelimit: Int,
    $calltimelimit: Int, $textsms: Int, $email: Int, $storage: Float,
    $calltime: Int, $userslimit: Int, $users: Int,
    $leadgatheringlimit: Int, $leadgathering: Int,
    $isfullaccess: Boolean, $trialplanenddate: String, $enddate: String,
    $isunlimitedusers: Boolean, $isunlimitedtextsms: Boolean,
    $isunlimitedemail: Boolean, $isunlimitedstorage: Boolean,
    $isunlimitedcalltime: Boolean, $isunlimitedleadgathering: Boolean,
    $socialpostlimit: Int, $socialpost: Int, $isunlimitedsocialpost: Boolean,
    $emailfinderlimit: Int, $emailfinder: Int, $isunlimitedemailfinder: Boolean,
    $leadfinderlimit: Int, $leadfinder: Int, $isunlimitedleadfinder: Boolean,
    $aiquerylimit: Int, $aiquery: Int, $isunlimitedaiquery: Boolean,
    $linkedinuserlimit: Int, $linkedinuser: Int,
    $whatsappuserlimit: Int, $whatsappuser: Int,
    $instagramuserlimit: Int, $instagramuser: Int,
    $enrichsearchlimit: Int, $enrichsearch: Int,
    $enrichreveallimit: Int, $enrichreveal: Int
  ) {
    updatebusinessusagebyid(
      businessusageid: $businessusageid, startdate: $startdate,
      trialplanid: $trialplanid,
      curruntplanid: $curruntplanid, subscriptionid: $subscriptionid,
      isfreeplan: $isfreeplan, plantype: $plantype,
      isactive: $isactive, paymentid: $paymentid, deleted: $deleted,
      textsmslimit: $textsmslimit, emaillimit: $emaillimit,
      storagelimit: $storagelimit, calltimelimit: $calltimelimit,
      textsms: $textsms, email: $email, storage: $storage,
      calltime: $calltime, userslimit: $userslimit, users: $users,
      leadgatheringlimit: $leadgatheringlimit, leadgathering: $leadgathering,
      isfullaccess: $isfullaccess, trialplanenddate: $trialplanenddate,
      enddate: $enddate, isunlimitedusers: $isunlimitedusers,
      isunlimitedtextsms: $isunlimitedtextsms, isunlimitedemail: $isunlimitedemail,
      isunlimitedstorage: $isunlimitedstorage, isunlimitedcalltime: $isunlimitedcalltime,
      isunlimitedleadgathering: $isunlimitedleadgathering,
      socialpostlimit: $socialpostlimit, socialpost: $socialpost,
      isunlimitedsocialpost: $isunlimitedsocialpost,
      emailfinderlimit: $emailfinderlimit, emailfinder: $emailfinder,
      isunlimitedemailfinder: $isunlimitedemailfinder,
      leadfinderlimit: $leadfinderlimit, leadfinder: $leadfinder,
      isunlimitedleadfinder: $isunlimitedleadfinder,
      aiquerylimit: $aiquerylimit, aiquery: $aiquery,
      isunlimitedaiquery: $isunlimitedaiquery,
      linkedinuserlimit: $linkedinuserlimit, linkedinuser: $linkedinuser,
      whatsappuserlimit: $whatsappuserlimit, whatsappuser: $whatsappuser,
      instagramuserlimit: $instagramuserlimit, instagramuser: $instagramuser,
      enrichsearchlimit: $enrichsearchlimit, enrichsearch: $enrichsearch,
      enrichreveallimit: $enrichreveallimit, enrichreveal: $enrichreveal
    )
  }
`;